import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import type { SortDirection } from 'ag-grid-community';

import { isSecondaryDimensionAInEffect } from 'components/V5GadgetForm/isSecondaryDimensionAEligible';
import limitModeTypeCheckers from 'components/V5GadgetForm/limitModeTypeCheckers';

import getGroupByField from './getGroupByField';
import fetchMatrixResponses, {
  PREVIOUS_SUBTOTALS_RESPONSE_INDEX,
  SUBTOTALS_RESPONSE_INDEX,
} from './fetch/fetchMatrixResponses';
import removePartialIntervalResponses from './fetch/removePartialIntervalResponses';
import flattenSecondaryDimensionRows from './format/flattenSecondaryDimensionRows';
import {
  buildCellComparator,
  buildGroupComparator,
} from './format/buildMatrixComparators';
import getRangeBuckets from './format/getRangeBuckets';
import addRankAndPercentageOfTotal from './format/addRankAndPercentageOfTotal';
import buildMatrixColDefs from './format/buildMatrixColDefs';
import buildRowData from './format/buildRowData';
import buildMatrixTotals from './format/buildMatrixTotals';
import buildComparisonRowData, {
  build2DDateComparisonRowData,
} from './format/buildComparisonRowData';
import {
  MatrixOverrides,
  fetchOverrideGroups,
  mergeOverrideGroups,
  toAliasedMatrix,
} from './overrideGroups';

export interface GetMetricMatrixProps {
  // What the matrix's own query fetches (every series, unless some ask their
  // own question: see overrides)
  chartDef: V5ChartDefinition;
  // Columns that ask their own question, fetched apart and drawn as columns
  // of their own, with the chart as drawn
  overrides?: MatrixOverrides;
  baseViews: FleetOps.BaseView[];
  datasets: FleetOps.DatasetDefinition[];
  filterInput: FilterInput;
  unitsLocale: string;
  usedMetrics: Metrics.Metric[];
  skip2DRowDataFormatting?: boolean;
  isDashboardGadget?: boolean;
  currentDateScope: DateRangeInput;
  previousDateScope: DateRangeInput;
  toMetricInput: (metric: Metrics.NormalMetric | MetricInput) => MetricInput;
  comparison?: PersistedComparisonType;
  client: ApolloClient<NormalizedCacheObject>;
  defaultCompareSort?: string;
  compareSortType?: MatrixCompareSortType;
  currentSortBy?: string;
  weekStartsOn: WeekStartsOn;
  formatMetric: (args: {
    metricId: string;
    value: string | number | null | undefined;
  }) => string;
  getEntityAndDefaultAppForField: ({ field }: { field: string }) =>
    | {
        entity: EntityDetails.Entity;
        app: EntityDetails.App;
      }
    | undefined;
  isSortAbsolute?: boolean;
  overrideSortField?: string;
  overrideSortDirection?: SortDirection;
  // Stacked secondary rows (drawn by DataTable): also build each dimension A
  // value's own row, from its subtotals
  isSecondaryRowsStacked?: boolean;
}

const getMetricMatrix = async ({
  chartDef: queryChartDef,
  overrides,
  baseViews,
  datasets,
  filterInput,
  unitsLocale,
  usedMetrics: queryUsedMetrics,
  skip2DRowDataFormatting,
  isDashboardGadget,
  currentDateScope,
  previousDateScope,
  toMetricInput,
  comparison,
  client,
  defaultCompareSort,
  compareSortType,
  currentSortBy,
  weekStartsOn,
  formatMetric: formatQueryMetric,
  getEntityAndDefaultAppForField,
  isSortAbsolute,
  overrideSortField,
  overrideSortDirection,
  isSecondaryRowsStacked,
}: GetMetricMatrixProps) => {
  const chartDef = queryChartDef;
  const reverseAxes = chartDef.reverseAxes || false;
  const isBHistogram =
    chartDef.dimensionB &&
    chartDef.dimensionB.field === 'date' &&
    (!!chartDef.trendByCalendarInterval || !!chartDef.trendByFixedIntervalDays);
  const isBDayOfWeek =
    chartDef.dimensionB &&
    chartDef.dimensionB.field === 'dayOfWeek' &&
    chartDef.groupByDayOfWeek;
  const isBMonth =
    chartDef.dimensionB &&
    chartDef.dimensionB.field === 'month' &&
    chartDef.groupByMonth;
  const isTermedHistogram =
    chartDef.dimensionA &&
    chartDef.dimensionB &&
    (isBHistogram || isBDayOfWeek || isBMonth);
  const isTopN =
    !!chartDef.dimensionA && !chartDef.dimensionB && chartDef.groupByLimitMode
      ? chartDef.groupByLimitMode === 'top n'
      : chartDef.groupByLimit && chartDef.groupByLimit <= 100;
  const isExcludeTopN =
    !!chartDef.dimensionA &&
    !chartDef.dimensionB &&
    limitModeTypeCheckers.isExcludeTopN(chartDef.groupByLimitMode);

  const groupByField = getGroupByField(chartDef);

  let responses = await fetchMatrixResponses({
    chartDef,
    filterInput,
    usedMetrics: queryUsedMetrics,
    isDashboardGadget,
    currentDateScope,
    previousDateScope,
    toMetricInput,
    comparison,
    client,
    groupByField,
    isTermedHistogram: !!isTermedHistogram,
    isTopN: !!isTopN,
    isExcludeTopN,
    isSecondaryRowsStacked,
  });

  // Rows (and previous-period rows) nest secondary-dimension terms; flatten
  // them to one row per (A, secondary A) pair. Totals stay keyed by date.
  responses = responses.map((response, index) =>
    index === 0 || index === 2
      ? flattenSecondaryDimensionRows({ chartDef, response })
      : response,
  );

  responses = removePartialIntervalResponses({ chartDef, responses });

  // Columns asking their own question, for the rows found
  if (overrides && groupByField) {
    const fetched = await fetchOverrideGroups({
      groups: overrides.groups,
      baseRows: responses[0],
      groupByField,
      currentDateScope,
      toMetricInput,
      client,
      isDashboardGadget,
    });
    responses = mergeOverrideGroups({
      chartDef: overrides.chartDef,
      responses,
      fetched,
    });
  }
  // Drawn with every series, each overriding one under its own id
  const drawn = overrides
    ? toAliasedMatrix({
        overrides,
        usedMetrics: queryUsedMetrics,
        formatMetric: formatQueryMetric,
      })
    : {
        chartDef: queryChartDef,
        usedMetrics: queryUsedMetrics,
        formatMetric: formatQueryMetric,
      };
  return formatMetricMatrix({
    ...drawn,
    responses,
    groupByField,
    reverseAxes,
    baseViews,
    datasets,
    unitsLocale,
    skip2DRowDataFormatting,
    currentDateScope,
    previousDateScope,
    comparison,
    defaultCompareSort,
    compareSortType,
    currentSortBy,
    weekStartsOn,
    getEntityAndDefaultAppForField,
    isSortAbsolute,
    overrideSortField,
    overrideSortDirection,
  });
};

// The grid from the fetched responses
const formatMetricMatrix = ({
  chartDef,
  usedMetrics,
  formatMetric,
  responses,
  groupByField,
  reverseAxes,
  baseViews,
  datasets,
  unitsLocale,
  skip2DRowDataFormatting,
  currentDateScope,
  previousDateScope,
  comparison,
  defaultCompareSort,
  compareSortType,
  currentSortBy,
  weekStartsOn,
  getEntityAndDefaultAppForField,
  isSortAbsolute,
  overrideSortField,
  overrideSortDirection,
}: Omit<
  GetMetricMatrixProps,
  | 'overrides'
  | 'filterInput'
  | 'client'
  | 'toMetricInput'
  | 'isDashboardGadget'
  | 'isSecondaryRowsStacked'
> & {
  responses: MetricsResponse[];
  groupByField: string | undefined;
  reverseAxes: boolean;
}) => {
  if (!groupByField) {
    console.warn(`Missing groupBy`);
    return {
      colDefs: [],
      rowData: [],
      totals: [],
    };
  }

  // Early-exit after partial-interval removal: if responses[0] is now empty
  // (can happen after removePartialIntervalResponses clears all rows)
  if (
    chartDef.excludePartialIntervals &&
    chartDef.dimensionA &&
    chartDef.dimensionA.fieldType === 'date' &&
    responses[0].length === 0
  ) {
    return {
      colDefs: [],
      rowData: [],
      totals: [],
    };
  }

  const isTwoDimensional = !!(
    chartDef.dimensionA &&
    chartDef.dimensionB &&
    responses[0].length &&
    responses[0].length > 0 &&
    responses[0][0].values
  );

  const cellComparator = buildCellComparator({
    isTwoDimensional,
    weekStartsOn,
  });
  const groupComparator = buildGroupComparator({
    chartDef,
    weekStartsOn,
    cellComparator,
  });
  const rangeBuckets = getRangeBuckets({
    isTwoDimensional,
    chartDef,
    responses,
  });

  const sharedColDefArgs = {
    chartDef,
    baseViews,
    currentDateScope,
    previousDateScope,
    comparison,
    currentSortBy,
    defaultCompareSort,
    compareSortType,
    isSortAbsolute,
    overrideSortField,
    overrideSortDirection,
    getEntityAndDefaultAppForField,
    cellComparator,
  };

  const colDefs = buildMatrixColDefs({
    ...sharedColDefArgs,
    usedMetrics,
    isTwoDimensional,
    groupByField,
    groupComparator,
    rangeBuckets,
    weekStartsOn,
    responses,
  });

  // Threshold colours need the unformatted value. Charts share this row data
  // and would pick up '-raw' keys as groups, so only keep them when needed.
  const hasCellColours =
    chartDef.gadgetType === 'matrix' &&
    chartDef.series.some((s) => !!s.cellColourConfig);

  const rowData = buildRowData({
    response: responses[0] as { [key: string]: number | string }[],
    isTwoDimensional,
    chartDef,
    usedMetrics,
    skip2DRowDataFormatting,
    groupByField,
    skipRaw: !hasCellColours,
    weekStartsOn,
    formatMetric,
    reverseAxes,
    totalsResponse: responses[1],
  });

  const totals = buildMatrixTotals({
    chartDef,
    reverseAxes,
    isTwoDimensional,
    rangeBuckets,
    responses,
    groupByField,
    usedMetrics,
    unitsLocale,
    rowData,
    formatMetric,
  });

  const comparisonRowData = (() => {
    // Skip comparison mode for reversed axes (not supported yet)
    if (reverseAxes) {
      return undefined;
    }

    if (!comparison) {
      return undefined;
    }

    const current = buildRowData({
      response: responses[0] as { [key: string]: number | string }[],
      isTwoDimensional,
      chartDef,
      usedMetrics,
      skip2DRowDataFormatting,
      groupByField,
      weekStartsOn,
      formatMetric,
      reverseAxes,
    });

    if (
      isTwoDimensional &&
      chartDef.dimensionA &&
      chartDef.dimensionB &&
      chartDef.dimensionB.fieldType === 'date'
    ) {
      // Hotfix/2D date matrix rendering blanks
      return build2DDateComparisonRowData({
        chartDef,
        usedMetrics,
        current,
        rangeBuckets,
        currentDateScope,
        unitsLocale,
      });
    }

    if (
      isTwoDimensional &&
      chartDef.dimensionB &&
      (chartDef.dimensionB.isGroupByDayOfWeek ||
        chartDef.dimensionB.rangeInput ||
        chartDef.dimensionB.isGroupByMonth ||
        // Categorical second dimension (terms x terms): show current values;
        // per-cell delta comparison isn't derived for this shape yet.
        chartDef.dimensionB.fieldType === 'text' ||
        chartDef.dimensionB.fieldType === 'boolean')
    ) {
      return rowData;
    }

    const previous = buildRowData({
      response: (responses[2] as { [key: string]: number | string }[]) || [],
      isTwoDimensional,
      chartDef,
      usedMetrics,
      skip2DRowDataFormatting,
      groupByField,
      weekStartsOn,
      formatMetric,
      reverseAxes,
    });

    // When reverseAxes is true, the groupByField changes
    const comparisonGroupByField = (() => {
      if (reverseAxes) {
        if (isTwoDimensional) {
          if (!chartDef.dimensionB) return groupByField;
          if (chartDef.dimensionB.isGroupByDayOfWeek) return 'dayOfWeek';
          if (chartDef.dimensionB.isGroupByMonth) return 'month';
          if (chartDef.dimensionB.fieldType === 'date') return 'date';
          if (chartDef.dimensionB.rangeInput) return 'key';
          return chartDef.dimensionB.field;
        } else {
          return 'metric';
        }
      }
      return groupByField;
    })();

    return buildComparisonRowData({
      current,
      previous,
      usedMetrics,
      chartDef,
      groupByField: comparisonGroupByField,
      unitsLocale,
      comparison,
      baseViews,
      datasets,
    });
  })();

  // Stacked secondary rows: each dimension A value's own row, from its
  // subtotals, built as the rows are (with comparison cells when comparing).
  // Drawn as the parent of that value's secondary rows
  const secondaryRowParents = (() => {
    const subtotals = responses[SUBTOTALS_RESPONSE_INDEX];
    if (!subtotals || !isSecondaryDimensionAInEffect(chartDef)) {
      return undefined;
    }
    const parentChartDef: V5ChartDefinition = {
      ...chartDef,
      secondaryDimensionA: undefined,
    };
    const parentRowArgs = {
      response: subtotals as { [key: string]: number | string }[],
      isTwoDimensional,
      chartDef: parentChartDef,
      usedMetrics,
      skip2DRowDataFormatting,
      groupByField,
      weekStartsOn,
      formatMetric,
      reverseAxes,
    };
    if (comparison && !reverseAxes) {
      const current = buildRowData(parentRowArgs);
      if (isTwoDimensional) {
        return build2DDateComparisonRowData({
          chartDef: parentChartDef,
          usedMetrics,
          current,
          rangeBuckets,
          currentDateScope,
          unitsLocale,
        });
      }
      // A column per metric: each value against its own previous row
      return buildComparisonRowData({
        current,
        previous: buildRowData({
          ...parentRowArgs,
          response: (responses[PREVIOUS_SUBTOTALS_RESPONSE_INDEX] ?? []) as {
            [key: string]: number | string;
          }[],
        }),
        usedMetrics,
        chartDef: parentChartDef,
        groupByField,
        unitsLocale,
        comparison,
        baseViews,
        datasets,
      });
    }
    return buildRowData({
      ...parentRowArgs,
      skipRaw: !hasCellColours,
      totalsResponse: responses[1],
    });
  })();
  // Stacked, the totals count the dimension A values shown, not their rows
  const shownTotals = secondaryRowParents
    ? totals.map((total) => ({
        ...total,
        [groupByField]: `Total (${secondaryRowParents.length})`,
      }))
    : totals;

  const withRankAndPercentage = <T extends { [key: string]: unknown }>(
    rows: T[],
  ) =>
    addRankAndPercentageOfTotal({
      rows,
      chartDef,
      response: responses[0],
      totals: responses[1] ?? [],
      groupByField,
      usedMetrics,
    });

  return {
    colDefs,
    rowData: withRankAndPercentage(rowData),
    comparisonRowData:
      comparisonRowData &&
      withRankAndPercentage<(typeof comparisonRowData)[number]>(
        comparisonRowData,
      ),
    totals: shownTotals,
    secondaryRowParents,
  };
};

export default getMetricMatrix;
